/**
 * Meta (Facebook) Pixel event helpers — twin of /lib/gtag.js for the
 * Meta ad account dataset 1346983080833361.
 *
 * The base fbq snippet in /public/index.html starts in
 * `fbq('consent', 'revoke')` mode so no events flow until the user
 * accepts marketing consent in the CookieConsent banner. The
 * `updateMetaConsent` helper below is invoked from /lib/gtag.js'
 * `updateConsent` so a single banner choice toggles both ad platforms.
 *
 * All event helpers are no-ops if window.fbq is missing (ad-blocker /
 * SSR / tests) so callers never need to guard.
 */

export const META_PIXEL_ID = "1346983080833361";

let consentGranted = false;

/** Mirror gtag's consent toggle: ads-on grants Meta, ads-off revokes. */
export function updateMetaConsent({ ads }) {
  if (typeof window === "undefined" || typeof window.fbq !== "function") return;
  if (ads) {
    window.fbq("consent", "grant");
    consentGranted = true;
  } else {
    window.fbq("consent", "revoke");
    consentGranted = false;
  }
}

/** True once the user has accepted ads/marketing consent. */
export function hasMetaConsent() {
  return consentGranted;
}

/** Read _fbp / _fbc browser cookies for server-side CAPI deduplication. */
export function getMetaCookies() {
  if (typeof document === "undefined") return { fbp: null, fbc: null };
  const read = (name) => {
    const m = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
    return m ? decodeURIComponent(m[1]) : null;
  };
  return { fbp: read("_fbp"), fbc: read("_fbc") };
}

/** Generate a UUID used as eventID on the Pixel + event_id on CAPI. */
export function newEventId() {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return crypto.randomUUID();
    }
  } catch (_) { /* fall through */ }
  return "px-" + Date.now() + "-" + Math.random().toString(36).slice(2, 11);
}

function fire(name, params, options) {
  if (typeof window === "undefined" || typeof window.fbq !== "function") return;
  if (options) window.fbq("track", name, params || {}, options);
  else window.fbq("track", name, params || {});
}

export const metaTrack = {
  /** PDP view — `ViewContent` is Meta's e-commerce standard event. */
  viewItem(product) {
    if (!product) return;
    fire("ViewContent", {
      content_ids: [product.sku],
      content_name: product.name,
      content_category: product.category || "",
      content_type: "product",
      value: Number(product.price || 0),
      currency: "GBP",
    });
  },

  /** Customer added a vial to the basket. */
  addToCart(product, quantity = 1) {
    if (!product) return;
    fire("AddToCart", {
      content_ids: [product.sku],
      content_name: product.name,
      content_type: "product",
      value: Number(product.price || 0) * quantity,
      currency: "GBP",
      contents: [{ id: product.sku, quantity, item_price: Number(product.price || 0) }],
    });
  },

  /** Customer landed on /checkout with items in the basket. */
  initiateCheckout(items, subtotal) {
    fire("InitiateCheckout", {
      content_ids: (items || []).map((i) => i.sku),
      content_type: "product",
      num_items: (items || []).reduce((s, i) => s + (i.quantity || 0), 0),
      value: Number(subtotal || 0),
      currency: "GBP",
      contents: (items || []).map((i) => ({
        id: i.sku, quantity: i.quantity, item_price: Number(i.price || 0),
      })),
    });
  },

  /**
   * Order paid — browser-side Purchase event.
   * Returns the eventID that was attached so the caller can echo it to
   * the backend (we already stored it on the order at creation time;
   * this is just defensive in case it ever drifts).
   */
  purchase(order, eventId) {
    if (!order) return null;
    const id = eventId || newEventId();
    fire(
      "Purchase",
      {
        content_ids: (order.items || []).map((i) => i.sku),
        content_type: "product",
        value: Number(order.total || 0),
        currency: "GBP",
        num_items: (order.items || []).reduce((s, i) => s + (i.quantity || 0), 0),
        order_id: order.order_number || order.id,
        contents: (order.items || []).map((i) => ({
          id: i.sku, quantity: i.quantity, item_price: Number(i.price || 0),
        })),
      },
      { eventID: id },
    );
    return id;
  },
};
